import { Info } from "lucide-react";
import { useId, useState, useRef } from "react";
import { useTranslation, Trans } from "react-i18next";
import { FiUpload, FiImage } from "react-icons/fi";
import { uploadSessionNotes } from "../../../application/sessionUseCases";
import { MAX_IMAGE_BYTES } from "../../../domain/notesAnalysis";
import ComponentCard from "../../../../../shared/ui/common/ComponentCard";
import Button from "../../../../../shared/ui/button/Button";
import { LoadingOverlay } from "../../../../../shared/ui/common/LoadingOverlay";

// Browsers do not agree on a MIME type for the iPhone camera format: Safari
// reports "image/heic" while Chrome leaves it empty, so the name is the fallback.
const IMAGE_EXTENSIONS = /\.(jpe?g|png|gif|webp|heic|heif)$/i;

const isSupportedImage = (f: File): boolean =>
  f.type.startsWith("image/") || IMAGE_EXTENSIONS.test(f.name);

interface Props {
  sessionId: string;
  onExtractedText?: (text: string) => void;
  onFileSelect?: (file: File | null) => void;
  onContinue?: () => void;
  onBack?: () => void;
}

export default function SessionAddUploadNotesForm({
  sessionId,
  onExtractedText,
  onFileSelect,
  onContinue,
  onBack,
}: Props) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const requirementsId = useId();
  const errorId = useId();
  const [loading, setLoading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);

  const handleFile = (f: File) => {
    if (!isSupportedImage(f)) {
      setError(t("session.notes.errorType", "Only image files are allowed"));
      setFile(null);
      return;
    }
    // Checked here rather than on submit so the therapist is not left waiting
    // on an upload the server is going to refuse with a 413.
    if (f.size > MAX_IMAGE_BYTES) {
      setError(t("session.notes.errorTooLarge", "This image is too large. Use one under 10 MB."));
      setFile(null);
      return;
    }
    setError(null);
    setFile(f);
    onFileSelect?.(f);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      handleFile(droppedFile);
    }
  };

  const handleContinue = async () => {
    if (!file) return onContinue?.();

    setLoading(true);
    setError(null);
    try {
      const data = new FormData();
      data.append("file", file);
      const updated = await uploadSessionNotes(sessionId, data);
      const extractedText = updated?.notes || "";
      onExtractedText?.(extractedText);
      onContinue?.();
    } catch (err) {
      console.error(err);
      // The name-based check above lets a mislabelled file through, so the
      // server has the last word on what the image actually is.
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 415) {
        setError(t("session.notes.errorType", "Only image files are allowed"));
      } else if (status === 413) {
        setError(t("session.notes.errorTooLarge", "This image is too large. Use one under 10 MB."));
      } else {
        setError(t("session.notes.errorUpload"));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <ComponentCard title={t("session.notes.title")}>
      <div className="space-y-6">
        {/* AI Frame */}
        <div className="ai-spectrum-border mb-6 rounded-xl">
          <div className="flex items-start gap-3 rounded-xl p-4">
            <Info className="mt-0.5 size-4 shrink-0 text-accent-text" />

            <p className="text-sm text-content-secondary leading-snug">
              <Trans
                i18nKey="session.notes.aiInfo"
                components={{ 1: <strong className="text-accent-text" /> }}
              />
            </p>
          </div>
        </div>

        {/* DROP ZONE */}
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragActive(true);
          }}
          onDragLeave={() => setDragActive(false)}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
          className={`
            cursor-pointer rounded-2xl border-2 border-dashed p-10
            flex flex-col items-center justify-center text-center
            transition
            ${dragActive
              ? "border-accent bg-accent-tint"
              : "border-gray-300 hover:border-accent"
            }
          `}
        >
          <FiUpload className="text-3xl text-accent-text mb-3" />

          <p className="font-medium">
            {t("session.notes.dragDrop", "Drag and drop your image here")}
          </p>
          <p className="text-sm text-gray-500 mt-1">
            {t("session.notes.browse", "or click to browse")}
          </p>

          <input
            ref={inputRef}
            type="file"
            accept="image/*,.heic,.heif"
            hidden
            aria-describedby={error ? errorId : requirementsId}
            aria-invalid={error ? true : undefined}
            onChange={(e) => {
              const selected = e.target.files?.[0];
              if (selected) handleFile(selected);
            }}
          />
        </div>

        {/* REQUIREMENTS — stated up front, not discovered by failing */}
        <p id={requirementsId} className="text-xs text-gray-500 text-center">
          {t(
            "session.notes.requirements",
            "Accepted formats: JPEG, PNG, GIF, WebP, HEIC/HEIF · Maximum size: 10 MB"
          )}
        </p>

        {/* FILE PREVIEW */}
        {file && (
          <div className="flex items-center gap-3 rounded-lg border p-4 bg-gray-50">
            <FiImage className="text-xl text-accent-text" />
            <div className="flex-1">
              <p className="text-sm font-medium">{file.name}</p>
              <p className="text-xs text-gray-500">
                {(file.size / 1024 / 1024).toFixed(2)} MB
              </p>
            </div>
          </div>
        )}

        {error && (
          <p id={errorId} role="alert" className="text-sm text-red-600 text-center">
            {error}
          </p>
        )}

        {/* ACTIONS */}
        <div className="flex justify-center gap-4 pt-6 border-t">
          <Button variant="outline" onClick={onBack} disabled={loading}>
            {t("session.back")}
          </Button>
          <Button onClick={handleContinue} disabled={!file || loading}>
            {t("session.continue")}
          </Button>
        </div>
      </div>

      <LoadingOverlay
        visible={loading}
        message={t("session.notes.processing")}
      />
    </ComponentCard>
  );
}
